import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import useCenterlineAiStyles from './styles';
import { BotState } from './core/chat-reducer';
import { stepLabel, TraceStep as TraceStepItem } from './core/trace';
import ToolIcon from './ToolIcon';
import { ChevronRightGlyph, SpinnerGlyph, ThinkingGlyph } from './Glyphs';

/**
 * The tool trace above an answer (chat-ui-spec §5.6–5.8): open while the turn runs, collapsed once
 * it settles. Live and restored turns use the same items.
 */

function TraceStep({ step }: { step: TraceStepItem }) {
  const classes = useCenterlineAiStyles();
  const running = step.status === 'running';
  const failed = step.status === 'error';
  return (
    <li
      className={clsx(
        classes.traceStep,
        step.parallel && classes.traceStepParallel,
        step.batchStart && classes.traceStepBatchStart,
      )}
    >
      <div className={classes.traceHead}>
        {running ? <SpinnerGlyph className={classes.spinner} /> : null}
        <code
          className={clsx(
            classes.traceTool,
            running && classes.traceToolRunning,
            failed && classes.traceToolError,
          )}
        >
          <ToolIcon tool={step.tool} size={13} />
          {step.tool || 'run_sql'}
        </code>
        <span className={classes.traceFlow} title={step.flowTitle}>
          {step.flow}
        </span>
      </div>
      <div
        className={clsx(
          classes.traceLabel,
          running && classes.traceLabelRunning,
          failed && classes.traceLabelError,
        )}
      >
        {stepLabel(step)}
      </div>
      {step.code ? (
        <pre className={clsx(classes.traceCode, failed && classes.traceCodeError)}>
          <code>{step.code}</code>
        </pre>
      ) : null}
      {step.images.map((src, k) => (
        <a
          // eslint-disable-next-line react/no-array-index-key
          key={k}
          className={classes.traceShot}
          href={src}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={src} alt="What the agent looked at" loading="lazy" />
        </a>
      ))}
    </li>
  );
}

export default function Trace({ bot }: { bot: BotState }) {
  const classes = useCenterlineAiStyles();
  const [open, setOpen] = useState(bot.traceOpen);
  // The turn opens and closes the trace as it runs and settles; the reader can toggle it between.
  useEffect(() => setOpen(bot.traceOpen), [bot.traceOpen]);

  return (
    <>
      <button
        type="button"
        className={clsx(classes.traceSummary, open && classes.traceSummaryOpen)}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronRightGlyph />
        {bot.summary}
      </button>
      {open && bot.trace.length > 0 ? (
        <ol className={classes.traceSteps} aria-label="What CENTERLINE AI did">
          {bot.trace.map((item, index) =>
            item.kind === 'step' ? (
              <TraceStep key={`s${item.i}-${index}`} step={item} />
            ) : item.text ? (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`r${index}`} className={classes.thinking}>
                <ThinkingGlyph />
                <span>{item.text}</span>
              </li>
            ) : null,
          )}
        </ol>
      ) : null}
    </>
  );
}
